import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { useModal } from '@/components/modals'
import { useFraudCard, useFraudReloads } from '@/lib/api/fraud-queries'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'
import { CardBrand } from './card-brand'
import { EMPTY, PILL, ROW, TD, TH, failureLabel, fmtDateTime, relativeTime, userDisplayName, walletName } from './format'
import { Banner, FinixLink, Spinner } from './shared'

/** Every charge with one card is fetched in one page; the backend caps at 200. */
const CHARGE_LIMIT = 200

const RED = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
const GREEN = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
const AMBER = 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400'

/** The card a "Recent card charges" row was charged to. */
export interface FraudChargedCard {
  /** Provider payment-instrument id; also the search key for its charges. */
  instrumentId: string
  brand?: string | null
  last4?: string | null
  wallet?: string | null
  cardType?: string | null
}

/** The red "Refunded" tag beside a charged amount. */
export function RefundedBadge() {
  return <span className={cn(PILL, RED)}>Refunded</span>
}

/**
 * One physical card: the accounts holding it at this organization's
 * events (others only counted), and every charge made with it at this
 * event. A user opens their risk profile; a transaction opens the
 * transaction modal; the Finix ids show only to staff, who can follow them.
 */
export function CardChargesModal({ eventId, card, money, onClose }: {
  eventId: number
  card: FraudChargedCard
  money: (value: number | string | null | undefined) => string
  onClose: () => void
}) {
  const { user: viewer } = useAuth()
  const { openTransactionModal } = useModal()
  const isStaff = !!viewer?.is_superuser
  const detail = useFraudCard(eventId, card.instrumentId)
  const charges = useFraudReloads(eventId, card.instrumentId, true, CHARGE_LIMIT)
  const rows = charges.data ?? []
  const known = detail.data?.card
  const wallet = walletName(known?.wallet ?? card.wallet)

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onEscape)
    document.body.classList.add('overflow-hidden')
    return () => {
      document.removeEventListener('keydown', onEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [onClose])

  const openTransaction = (transactionId: number) => {
    onClose()
    setTimeout(() => openTransactionModal(transactionId, eventId), 100)
  }

  return (
    <div className="fixed inset-0 z-110 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Card"
        className="relative flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl dark:border-neutral-800 dark:bg-neutral-900"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 p-3 dark:border-neutral-800">
          <div>
            <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              <CardBrand brand={known?.brand ?? card.brand} last4={known?.last4 ?? card.last4} wallet={known?.wallet ?? card.wallet} />
              {(known?.card_type || card.cardType) && <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">{known?.card_type || card.cardType}</span>}
              {wallet && <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">{wallet}</span>}
              {known?.blocked && <span className={cn(PILL, RED)}>Blocked</span>}
              {known?.trusted && <span className={cn(PILL, GREEN)}>Trusted</span>}
              {known?.verification_state === 'failed' && (
                <span className={cn(PILL, RED)} title={known.verification_failure_code || undefined}>
                  Verification failed{known.verification_failure_code ? ` · ${failureLabel(known.verification_failure_code)}` : ''}
                </span>
              )}
            </h2>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-neutral-500 dark:text-neutral-400">
              {known?.bin && <span>BIN {known.bin}</span>}
              {known?.issuer_country && <span>Issuer {known.issuer_country}</span>}
              {isStaff && <FinixLink id={card.instrumentId} kind="instrument" />}
            </p>
          </div>
          <button
            className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-auto p-3">
          <section>
            <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Accounts with this card</h3>
            {detail.error ? (
              <Banner kind="error">Could not load the card's accounts: {detail.error.message}</Banner>
            ) : detail.isLoading || !detail.data ? (
              <div className="flex items-center justify-center py-6"><Spinner /></div>
            ) : detail.data.accounts.length === 0 && detail.data.other_accounts === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">No account holds this card.</p>
            ) : (
              <>
                {detail.data.accounts.length > 0 && (
                  <table className="w-full whitespace-nowrap">
                    <thead><tr className="border-b border-neutral-200 dark:border-neutral-800">
                      <th className={TH}>User</th><th className={TH}>Charges here</th><th className={TH}>Added</th><th className={TH}>State</th>
                    </tr></thead>
                    <tbody>
                      {detail.data.accounts.map((a) => (
                        <tr key={a.user.id} className={cn(ROW, a.disabled && 'opacity-50')}>
                          <td className={TD}>
                            <Link
                              to="/events/$eventId/fraud/users/$userId"
                              params={{ eventId: String(eventId), userId: String(a.user.id) }}
                              onClick={onClose}
                              className="text-blue-600 hover:underline dark:text-blue-400"
                            >
                              {userDisplayName(a.user)}
                            </Link>
                            {a.user.email && <div className="text-xs text-neutral-500">{a.user.email}</div>}
                          </td>
                          <td className={TD}>
                            {a.charges_here}
                            {a.last_charge_at && <span className="ml-1 text-xs text-neutral-500">· last {relativeTime(a.last_charge_at)}</span>}
                          </td>
                          <td className={TD}>
                            {fmtDateTime(a.added_at)}
                            {a.wallet && <span className="ml-1 text-xs text-neutral-500">· {walletName(a.wallet)}{a.instruments > 1 ? ` × ${a.instruments}` : ''}</span>}
                          </td>
                          <td className={TD}>
                            <div className="flex flex-wrap items-center gap-1.5">
                              {a.blocked && <span className={cn(PILL, RED)}>Blocked</span>}
                              {a.trusted && <span className={cn(PILL, GREEN)}>Trusted</span>}
                              {a.verification_state === 'failed' && <span className={cn(PILL, RED)} title={a.verification_failure_code || undefined}>Verification failed</span>}
                              {!a.wallet && !a.three_ds_verified && <span className={cn(PILL, AMBER)}>Not 3DS verified</span>}
                              {a.disabled && <span className="text-xs text-neutral-500">removed</span>}
                              {!a.blocked && !a.trusted && a.verification_state !== 'failed' && (a.wallet || a.three_ds_verified) && !a.disabled && (
                                <span className="text-neutral-400 dark:text-neutral-600">{EMPTY}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {detail.data.other_accounts > 0 && (
                  <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                    {detail.data.other_accounts} other account{detail.data.other_accounts === 1 ? '' : 's'} hold{detail.data.other_accounts === 1 ? 's' : ''} this card at other organizations' events.
                  </p>
                )}
              </>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Charges at this event</h3>
            {charges.error ? (
              <Banner kind="error">Could not load this card's charges: {charges.error.message}</Banner>
            ) : charges.isLoading ? (
              <div className="flex items-center justify-center py-6"><Spinner /></div>
            ) : rows.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">No charges with this card at this event.</p>
            ) : (
              <table className="w-full whitespace-nowrap">
                <thead><tr className="border-b border-neutral-200 dark:border-neutral-800">
                  <th className={TH}>When</th><th className={TH}>User</th><th className={TH}>Transaction</th><th className={TH}>Charged</th>
                  {isStaff && <th className={TH}>Transfer</th>}
                  <th className={TH}>Dispute</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={ROW}>
                      <td className={TD}>{fmtDateTime(r.timestamp)}</td>
                      <td className={TD}>
                        {r.user ? (
                          <Link
                            to="/events/$eventId/fraud/users/$userId"
                            params={{ eventId: String(eventId), userId: String(r.user.id) }}
                            onClick={onClose}
                            className="text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {userDisplayName(r.user)}
                          </Link>
                        ) : EMPTY}
                      </td>
                      <td className={TD}>
                        <button
                          type="button"
                          onClick={() => openTransaction(r.id)}
                          className="text-blue-600 hover:underline dark:text-blue-400"
                          title="Open the transaction"
                        >
                          #{r.id}
                        </button>
                      </td>
                      <td className={TD}>
                        <span className="tabular-nums">{money(r.charged)}</span>
                        {r.refunded ? <span className="ml-1.5"><RefundedBadge /></span> : null}
                      </td>
                      {isStaff && <td className={TD}><FinixLink id={r.provider_transfer_id} /></td>}
                      <td className={TD}>
                        {r.chargeback_id
                          ? <span className={cn(PILL, AMBER)}>Chargeback</span>
                          : <span className="text-neutral-400 dark:text-neutral-600">{EMPTY}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
